import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import BannerCarousel from '../components/BannerCarousel';
import Countdown from '../components/Countdown';
import ReservationForm from '../components/ReservationForm';
import Marquee from '../components/Marquee';
import Button from '../components/ui/Button';
import SectionHeading from '../components/ui/SectionHeading';
import Reveal from '../components/ui/Reveal';
import { Container, Section } from '../components/ui/Layout';
import MENU from '../data/menu';
import {
  FOTO_MURO,
  FLYER_NOCHE_DE_CHICAS,
  FLYER_FANFEST_MEXICO_INGLATERRA,
  FLYER_FANFEST_MAPA,
  HERO_VIDEOS
} from '../assets/media';

const MARQUEE_ITEMS = [
  'Toma rico',
  'Llora bonito',
  'Supéralo',
  'Reserva tu mesa',
  'Cocteles de autor',
  'Karaoke del despecho'
];

/* ================= Menú destacado ================= */

const MenuColumns = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const MenuCol = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  transition: transform 350ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 350ms ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 18px 34px rgba(0, 0, 0, 0.14);
  }
`;

const MenuColPhoto = styled.div`
  position: relative;
  height: 150px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url(${({ $src }) => $src});
    background-size: cover;
    background-position: center;
    transition: transform 500ms ease;
  }

  ${MenuCol}:hover &::before {
    transform: scale(1.1);
  }
`;

const MenuColTitle = styled.h3`
  position: absolute;
  left: 1rem;
  bottom: 0.5rem;
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ffffff;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
`;

const MenuColBody = styled.div`
  padding: 1.25rem 1.5rem;
`;

const DishItem = styled.div`
  padding: 0.8rem 0;

  &:not(:last-child) {
    border-bottom: 2px solid ${({ theme }) => theme.colors.gold};
    border-image: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.gold} 32px,
        transparent 32px
      )
      1;
  }
`;

const DishTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
`;

const DishName = styled.span`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.text};
`;

const DishPrice = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
`;

const DishDesc = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.82rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.textFaint};
`;

const FullWidthCta = styled(Button)`
  width: 100%;
`;

/* ============ Recomendaciones de la casa ============ */

const RecoBand = styled.div`
  margin-top: 3.5rem;
  background: ${({ theme }) => theme.colors.bgDarker};
  display: grid;
  clip-path: polygon(0 3.5%, 100% 0%, 100% 96.5%, 0% 100%);
  padding: 0.5rem 0;
  transition: transform 350ms ease;

  &:hover {
    transform: translateY(-4px);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 280px 1fr;
  }
`;

const RecoCard = styled.div`
  background: ${({ theme }) => theme.colors.gold};
  padding: 2rem 1.75rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RecoCardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  margin: 0 0 0.25rem;
  color: ${({ theme }) => theme.colors.text};
`;

const RecoCardSub = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: rgba(29, 27, 23, 0.7);
`;

const RecoList = styled.div`
  padding: 2rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  justify-content: center;
`;

const RecoItem = styled.div``;

const RecoKicker = styled.span`
  display: block;
  font-style: italic;
  font-size: 0.82rem;
  color: #a8a396;
`;

const RecoName = styled.span`
  display: block;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #ffffff;

  &::after {
    content: '';
    display: block;
    width: 30px;
    height: 3px;
    background: ${({ theme }) => theme.colors.gold};
    margin-top: 0.35rem;
  }
`;

/* ================= Nosotros ================= */

const AboutBand = styled.div`
  display: grid;
  gap: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1.4fr;
  }
`;

const AboutDark = styled.div`
  background:
    linear-gradient(rgba(20, 18, 14, 0.78), rgba(20, 18, 14, 0.82)),
    url(${FOTO_MURO}) center 20%/cover;
  color: #f4f3ee;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 1rem;
`;

const AboutKicker = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.24em;
  color: #cfccc3;
`;

const AboutTitle = styled.h3`
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: #ffffff;
  margin: 0;

  &::after {
    content: '';
    display: block;
    width: 34px;
    height: 3px;
    background: ${({ theme }) => theme.colors.gold};
    margin-top: 0.6rem;
  }
`;

const AboutDarkText = styled.p`
  color: #d8d5cc;
  margin: 0;
`;

const StoryBlock = styled.div`
  padding: 3rem 2rem;
  display: grid;
  gap: 2rem;
  align-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const StoryTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: 0.14em;

  &::after {
    content: '';
    display: block;
    width: 34px;
    height: 3px;
    background: ${({ theme }) => theme.colors.gold};
    margin-top: 0.6rem;
  }
`;

const StoryHighlight = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: ${({ theme }) => theme.colors.text};

  &::before {
    content: '';
    display: block;
    width: 34px;
    height: 3px;
    background: ${({ theme }) => theme.colors.gold};
    margin-bottom: 0.6rem;
  }
`;

/* ================= Eventos ================= */

const FeaturedEvent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 1rem;
`;

const EventPhoto = styled.div`
  position: relative;
  min-height: 300px;
  background:
    linear-gradient(rgba(20, 18, 14, 0.35), rgba(20, 18, 14, 0.6)),
    url(${({ $src }) => $src}) center/cover;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const EventNumber = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.6rem;
  font-weight: 800;
  color: #ffffff;
  padding: 1rem 1.25rem;
`;

const EventPhotoBottom = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: rgba(15, 14, 11, 0.65);
`;

const EventInfo = styled.div`
  display: grid;
  gap: 1.5rem;
  padding: 1.75rem 1.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr auto;
  }
`;

const EventTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 0.5rem;
`;

const DateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 170px;
`;

const DateTop = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const DateDay = styled.span`
  background: ${({ theme }) => theme.colors.gold};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.5rem;
  font-weight: 800;
  padding: 0.5rem 0.9rem;
  display: flex;
  align-items: center;
`;

const DateMonth = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  padding: 0.5rem 0.9rem;
  display: flex;
  align-items: center;
`;

const DateStart = styled.span`
  background: ${({ theme }) => theme.colors.bgDarker};
  color: #f4f3ee;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  text-align: center;
  padding: 0.55rem;
`;

const DateEntry = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  text-align: center;
  padding: 0.55rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SmallEventRow = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  margin-bottom: 0.75rem;
`;

const SmallEventNumber = styled.span`
  background: ${({ theme }) => theme.colors.gold};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.3rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  padding: 0 1.1rem;
`;

const SmallEventBody = styled.div`
  flex: 1;
  padding: 0.8rem 1.1rem;
`;

const SmallEventTitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${({ theme }) => theme.colors.text};
`;

const SmallEventDate = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.textFaint};
`;

const SmallEventDesc = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.88rem;
`;

const ExpandButton = styled.button`
  width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  border-left: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.text};
  }
`;

/* ================= Galería ================= */

const FilterBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;

const FilterButton = styled.button`
  min-height: 44px;
  padding: 0.4em 0.6em;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: ${({ theme }) => theme.colors.text};
  box-shadow: ${({ theme, $active }) =>
    $active ? `inset 0 -8px 0 0 ${theme.colors.gold}` : 'none'};
  transition: box-shadow 200ms ease;

  &:hover {
    box-shadow: inset 0 -8px 0 0 ${({ theme }) => theme.colors.gold};
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const GalleryTile = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.15);
    opacity: 0;
    transition: opacity 300ms ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const galleryMediaStyle = `
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 500ms cubic-bezier(0.16, 1, 0.3, 1);

  ${GalleryTile}:hover & {
    transform: scale(1.14);
  }
`;

const GalleryItem = styled.img`
  ${galleryMediaStyle}
`;

const GalleryVideo = styled.video`
  ${galleryMediaStyle}
`;

const PlayBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.85);
  z-index: 2;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  opacity: 0;
  transition: opacity 250ms ease, transform 250ms ease;

  ${GalleryTile}:hover & {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

/* ============ Reservaciones ============ */

const PhoneBar = styled.div`
  max-width: 860px;
  margin: 0 auto 2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  padding: 1.1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;

  a {
    background: ${({ theme }) => theme.colors.gold};
    padding: 0.2em 0.6em;
    font-size: 1.05rem;
    letter-spacing: 0.1em;
  }

  a:hover {
    background: ${({ theme }) => theme.colors.pinkSoft};
  }
`;

const OrDivider = styled.p`
  max-width: 860px;
  margin: 0 auto 1.75rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 1.1rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text};

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px solid ${({ theme }) => theme.colors.text};
  }
`;

/* ============ Contacto y horarios ============ */

const HoursGrid = styled.div`
  display: grid;
  gap: 1rem;
  max-width: 860px;
  margin: 0 auto 2.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const HourBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  padding: 1.5rem;
`;

const HourBoxTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  margin-bottom: 0.75rem;

  mark {
    background: ${({ theme }) => theme.colors.gold};
    padding: 0 0.2em;
  }
`;

const HourBoxText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  font-style: italic;

  &::before {
    content: '';
    display: block;
    width: 26px;
    height: 3px;
    background: ${({ theme }) => theme.colors.gold};
    margin-bottom: 0.6rem;
  }
`;

const ContactCols = styled.div`
  display: grid;
  gap: 2rem;
  max-width: 860px;
  margin: 0 auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ContactCol = styled.div`
  h4 {
    font-size: 0.9rem;
    font-weight: 800;
    letter-spacing: 0.14em;

    &::after {
      content: '';
      display: block;
      width: 26px;
      height: 3px;
      background: ${({ theme }) => theme.colors.gold};
      margin-top: 0.5rem;
    }
  }

  p, a {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.textMuted};
    display: block;
    margin: 0 0 0.25rem;
  }

  a:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

/* ================= Datos fijos ================= */

const EVENTS = [
  {
    id: 1,
    numero: '01',
    titulo: 'Noche de Chicas',
    fecha: '2026-07-09T21:00:00',
    dia: '09',
    mes: 'JUL 2026',
    inicio: 'Empieza: 9:00 PM',
    entrada: 'Con Sunday Night Project',
    descripcion:
      'Jueves de banda en vivo con Sunday Night Project. Noche pensada para ellas: llega con tus amigas a bailar y a tirar el despecho en bola.',
    imagen: FLYER_NOCHE_DE_CHICAS
  },
  {
    id: 2,
    numero: '02',
    titulo: 'La Chingada FanFest: México vs Inglaterra',
    fechaTexto: 'Domingo 2 Ago 2026 · Al terminar el partido',
    descripcion:
      'Vemos el partido en pantalla grande y al terminar tocan Los Éxtasis, Banda Tierra Alteña y Kiluy Arias y Los Entusiastas.'
  },
  {
    id: 3,
    numero: '03',
    titulo: 'Mezcal & Vinilos: Noche de Canciones Tristes',
    fechaTexto: '8 Ago 2026 @ 8:00 PM',
    descripcion:
      'Cata de mezcales con puro clásico para llorar bonito, en tornamesa y a media luz.'
  }
];

const GALLERY = [
  { id: 1, type: 'image', cat: 'interior', alt: 'Muro fotográfico de Vete a la Chingada', src: FOTO_MURO },
  { id: 2, type: 'image', cat: 'eventos', alt: 'Flyer Noche de Chicas con Sunday Night Project', src: FLYER_NOCHE_DE_CHICAS },
  { id: 3, type: 'image', cat: 'eventos', alt: 'Flyer FanFest México vs Inglaterra', src: FLYER_FANFEST_MEXICO_INGLATERRA },
  { id: 4, type: 'image', cat: 'eventos', alt: 'Mapa de mesas del FanFest', src: FLYER_FANFEST_MAPA },
  { id: 5, type: 'video', cat: 'eventos', alt: 'Video del ambiente del bar', src: HERO_VIDEOS[0] },
  { id: 6, type: 'video', cat: 'eventos', alt: 'Video de una noche en el bar', src: HERO_VIDEOS[1] },
  { id: 7, type: 'video', cat: 'eventos', alt: 'Video de un evento en el bar', src: HERO_VIDEOS[2] },
  { id: 8, type: 'image', cat: 'tragos', alt: 'Coctel de autor con mezcal', src: 'https://placehold.co/600x600/24211b/f5e71e?text=Cocteles' }
];

const GALLERY_FILTERS = [
  { value: 'todo', label: 'Todas' },
  { value: 'tragos', label: 'Tragos y Botanas' },
  { value: 'interior', label: 'Interior' },
  { value: 'eventos', label: 'Eventos' }
];

const CATEGORY_PHOTOS = [
  'https://placehold.co/600x300/1d1b17/f5e71e?text=+',
  'https://placehold.co/600x300/24211b/f5e71e?text=+',
  'https://placehold.co/600x300/16140f/f5e71e?text=+'
];

// Rangos en minutos por día (0=Dom ... 6=Sáb); end > 1440 cruza medianoche.
const SCHEDULE = {
  0: [{ start: 780, end: 1320 }],
  1: [],
  2: [{ start: 1080, end: 1500 }],
  3: [{ start: 1080, end: 1500 }],
  4: [{ start: 1080, end: 1500 }],
  5: [{ start: 780, end: 1620 }],
  6: [{ start: 780, end: 1620 }]
};

function isOpenNow(now = new Date()) {
  const day = now.getDay();
  const minutes = now.getHours() * 60 + now.getMinutes();

  const today = SCHEDULE[day].some((r) => minutes >= r.start && minutes < r.end);
  const yesterday = SCHEDULE[(day + 6) % 7].some(
    (r) => r.end > 1440 && minutes < r.end - 1440
  );

  return today || yesterday;
}

/* ================= Página ================= */

function Home() {
  const [galleryFilter, setGalleryFilter] = useState('todo');
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const highlights = useMemo(
    () =>
      MENU.filter((cat) => cat.items.length > 0)
        .slice(0, 3)
        .map((cat) => ({ ...cat, items: cat.items.slice(0, 3) })),
    []
  );

  const recommendations = useMemo(
    () =>
      MENU.filter((cat) => cat.items.length > 0)
        .slice(0, 3)
        .map((cat) => ({ categoria: cat.nombre, nombre: cat.items[0].nombre })),
    []
  );

  const galleryItems = useMemo(
    () =>
      galleryFilter === 'todo'
        ? GALLERY
        : GALLERY.filter((g) => g.cat === galleryFilter),
    [galleryFilter]
  );

  const [featured, ...otherEvents] = EVENTS;
  const open = isOpenNow(now);

  return (
    <>
      <BannerCarousel />
      <Marquee items={MARQUEE_ITEMS} />

      {/* Menú del día */}
      <Section id="menu-home">
        <Container>
          <Reveal>
            <SectionHeading
              kicker="El menú de hoy"
              title="Delicioso"
              subtitle="Cocteles · Botanas · Mezcales"
              serif="Menú"
            />
          </Reveal>

          {highlights.length > 0 && (
            <>
              <MenuColumns>
                {highlights.map((cat, i) => (
                  <Reveal key={cat.id} delay={i * 120} as={MenuCol}>
                    <MenuColPhoto $src={CATEGORY_PHOTOS[i % CATEGORY_PHOTOS.length]}>
                      <MenuColTitle>{cat.nombre}</MenuColTitle>
                    </MenuColPhoto>
                    <MenuColBody>
                      {cat.items.map((item) => (
                        <DishItem key={item.id}>
                          <DishTop>
                            <DishName>{item.nombre}</DishName>
                            <DishPrice>${Number(item.precio).toFixed(0)}</DishPrice>
                          </DishTop>
                          {item.descripcion && <DishDesc>{item.descripcion}</DishDesc>}
                        </DishItem>
                      ))}
                    </MenuColBody>
                  </Reveal>
                ))}
              </MenuColumns>
              <FullWidthCta as={Link} to="/menu" $variant="dark">
                Ver el menú completo
              </FullWidthCta>
            </>
          )}

          {recommendations.length > 0 && (
            <Reveal>
              <RecoBand>
                <RecoCard>
                  <RecoCardTitle>De la Casa</RecoCardTitle>
                  <RecoCardSub>Recomendaciones</RecoCardSub>
                </RecoCard>
                <RecoList>
                  {recommendations.map((reco) => (
                    <RecoItem key={reco.nombre}>
                      <RecoKicker>{reco.categoria}</RecoKicker>
                      <RecoName>{reco.nombre}</RecoName>
                    </RecoItem>
                  ))}
                </RecoList>
              </RecoBand>
            </Reveal>
          )}
        </Container>
      </Section>

      {/* Nosotros */}
      <Section id="nosotros">
        <Container>
          <Reveal>
            <SectionHeading
              kicker="Nuestra historia"
              title="Nosotros"
              subtitle="El bar donde se supera"
              serif="Despecho"
            />
          </Reveal>
          <Reveal delay={150}>
            <AboutBand>
              <AboutDark>
                <AboutKicker>Quiénes somos</AboutKicker>
                <AboutTitle>Somos</AboutTitle>
                <AboutDarkText>
                  Un bar chiquito y honesto donde llegas con el corazón hecho pedazos y
                  sales con un mezcal en la mano y la playlist correcta de fondo.
                </AboutDarkText>
                <AboutDarkText>
                  Y sí: cuando hay fiesta, hay fiesta en serio.
                </AboutDarkText>
                <Button as={Link} to="/reservaciones">
                  Reservar mesa
                </Button>
              </AboutDark>
              <StoryBlock>
                <div>
                  <StoryTitle>La Historia</StoryTitle>
                  <p>
                    Queríamos un lugar donde tronar no fuera el fin del mundo sino el
                    inicio de la noche: tragos honestos, botanas buenas y música
                    triste que se disfruta.
                  </p>
                  <p>No lo encontramos, así que lo abrimos nosotros.</p>
                </div>
                <div>
                  <p>
                    Aquí no juzgamos tus mensajes de las 3am — celebramos que ya los
                    borraste. Ven solo, ven en bola, ven directo del "estamos bien"
                    que resultó ser mentira.
                  </p>
                  <StoryHighlight>
                    Desahogarse acompañado es primero.
                  </StoryHighlight>
                </div>
              </StoryBlock>
            </AboutBand>
          </Reveal>
        </Container>
      </Section>

      {/* Eventos */}
      <Section id="eventos">
        <Container>
          <Reveal>
            <SectionHeading
              kicker="Próximamente"
              title="Increíbles"
              subtitle="Aparta la fecha y el corazón"
              serif="Eventos"
            />
          </Reveal>

          <Reveal delay={100} variant="scale">
            <FeaturedEvent>
              <EventPhoto $src={featured.imagen}>
                <EventNumber>{featured.numero}</EventNumber>
                <EventPhotoBottom>
                  <Countdown target={featured.fecha} />
                  <Button as={Link} to="/reservaciones">
                    Reservar mesa
                  </Button>
                </EventPhotoBottom>
              </EventPhoto>
              <EventInfo>
                <div>
                  <EventTitle>{featured.titulo}</EventTitle>
                  <p style={{ margin: 0 }}>{featured.descripcion}</p>
                </div>
                <DateBox>
                  <DateTop>
                    <DateDay>{featured.dia}</DateDay>
                    <DateMonth>{featured.mes}</DateMonth>
                  </DateTop>
                  <DateStart>{featured.inicio}</DateStart>
                  <DateEntry>{featured.entrada}</DateEntry>
                </DateBox>
              </EventInfo>
            </FeaturedEvent>
          </Reveal>

          {otherEvents.map((ev, i) => (
            <Reveal key={ev.id} delay={i * 80} variant={i % 2 === 0 ? 'left' : 'right'}>
              <SmallEventRow>
                <SmallEventNumber>{ev.numero}</SmallEventNumber>
                <SmallEventBody>
                  <SmallEventTitle>{ev.titulo}</SmallEventTitle>
                  <SmallEventDate>{ev.fechaTexto}</SmallEventDate>
                  {expandedEvent === ev.id && (
                    <SmallEventDesc>{ev.descripcion}</SmallEventDesc>
                  )}
                </SmallEventBody>
                <ExpandButton
                  type="button"
                  aria-expanded={expandedEvent === ev.id}
                  aria-label={`Ver detalles de ${ev.titulo}`}
                  onClick={() => setExpandedEvent((cur) => (cur === ev.id ? null : ev.id))}
                >
                  {expandedEvent === ev.id ? '−' : '+'}
                </ExpandButton>
              </SmallEventRow>
            </Reveal>
          ))}
        </Container>
      </Section>

      {/* Galería */}
      <Section id="galeria">
        <Container>
          <Reveal>
            <SectionHeading
              kicker="Échale un ojo a nuestra"
              title="Foto"
              subtitle="Tragos · Interior · Eventos"
              serif="Galería"
            />
          </Reveal>
          <FilterBar role="tablist" aria-label="Filtros de galería">
            {GALLERY_FILTERS.map((f) => (
              <FilterButton
                key={f.value}
                type="button"
                role="tab"
                aria-selected={galleryFilter === f.value}
                $active={galleryFilter === f.value}
                onClick={() => setGalleryFilter(f.value)}
              >
                {f.label}
              </FilterButton>
            ))}
          </FilterBar>
          <GalleryGrid>
            {galleryItems.map((g, i) => (
              <Reveal
                key={g.id}
                delay={(i % 4) * 80}
                variant={i % 2 === 0 ? 'up' : 'scale'}
                as={GalleryTile}
              >
                {g.type === 'video' ? (
                  <>
                    <GalleryVideo
                      src={g.src}
                      aria-label={g.alt}
                      muted
                      loop
                      playsInline
                      autoPlay
                      preload="none"
                    />
                    <PlayBadge aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </PlayBadge>
                  </>
                ) : (
                  <GalleryItem src={g.src} alt={g.alt} loading="lazy" />
                )}
              </Reveal>
            ))}
          </GalleryGrid>
        </Container>
      </Section>

      {/* Reservaciones */}
      <Section id="reservaciones-home">
        <Container>
          <Reveal>
            <SectionHeading
              kicker="Aparta tu lugar"
              title="Reservaciones"
              subtitle="Dinos fecha y hora, te guardamos buena mesa"
              serif="Mesa"
            />
          </Reveal>
          <PhoneBar>
            <span>Llámanos para reservar tu mesa:</span>
            <a href="tel:+527770000000">777 000 0000</a>
          </PhoneBar>
          <OrDivider>o vía el formulario</OrDivider>
          <Reveal>
            <ReservationForm />
          </Reveal>
        </Container>
      </Section>

      {/* Contacto y horarios */}
      <Section id="contacto-info">
        <Container>
          <Reveal>
            <SectionHeading
              kicker="Aquí andamos"
              title="Contacto"
              subtitle="Horarios · Dirección · Redes"
              serif="Horarios"
            />
          </Reveal>
          <Reveal>
            <HoursGrid>
              <HourBox>
                <HourBoxTitle>
                  Martes <mark>–</mark> Jueves
                </HourBoxTitle>
                <HourBoxText>18:00 – 01:00</HourBoxText>
              </HourBox>
              <HourBox>
                <HourBoxTitle>
                  Viernes <mark>–</mark> Domingo
                </HourBoxTitle>
                <HourBoxText>
                  Vie/Sáb 13:00 – 03:00 · Dom 13:00 – 22:00. Lunes cerrado.
                </HourBoxText>
              </HourBox>
              <HourBox>
                <HourBoxTitle>
                  Hora: {now.toLocaleTimeString('es-MX')}
                  <br />
                  <mark>{open ? 'Abierto ahora' : 'Cerrado'}</mark>
                </HourBoxTitle>
                <HourBoxText>
                  {open
                    ? 'Vente antes de que se te pase la tusa.'
                    : 'Vuelve en horario de despecho.'}
                </HourBoxText>
              </HourBox>
            </HoursGrid>
          </Reveal>

          <ContactCols>
            <Reveal delay={0} variant="left" as={ContactCol}>
              <h4>Visítanos</h4>
              <p>Av. Corazón Roto #123, Col. Centro</p>
              <p>Ciudad de México</p>
            </Reveal>
            <Reveal delay={100} variant="up" as={ContactCol}>
              <h4>Contáctanos</h4>
              <a href="tel:+527770000000">777 000 0000</a>
              <p>WhatsApp disponible</p>
            </Reveal>
            <Reveal delay={200} variant="right" as={ContactCol}>
              <h4>Síguenos</h4>
              <a
                href="https://instagram.com/vetealachingadaar_oficial"
                target="_blank"
                rel="noreferrer"
              >
                Instagram @vetealachingadaar_oficial
              </a>
            </Reveal>
          </ContactCols>
        </Container>
      </Section>
    </>
  );
}

export default Home;
